import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCheck,
  CheckCircle2,
  ClipboardList,
  Clock,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";

const TASK_TYPES = [
  { v: "call_back", l: "Call back" },
  { v: "send_docs", l: "Send docs" },
  { v: "meeting", l: "Meeting" },
  { v: "follow_up", l: "Follow up" },
  { v: "custom", l: "Custom" },
];

const BUCKETS = [
  { k: "overdue", l: "Overdue", icon: AlertTriangle, accent: "text-rose-700 bg-rose-50 border-rose-200" },
  { k: "today", l: "Today", icon: Clock, accent: "text-amber-700 bg-amber-50 border-amber-200" },
  { k: "this_week", l: "This week", icon: Clock, accent: "text-blue-700 bg-blue-50 border-blue-200" },
  { k: "later", l: "Later", icon: Clock, accent: "text-slate-700 bg-slate-50 border-slate-200" },
  { k: "done", l: "Done", icon: CheckCheck, accent: "text-emerald-700 bg-emerald-50 border-emerald-200" },
];

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffMs = d.getTime() - Date.now();
  const abs = Math.abs(diffMs);
  const m = Math.round(abs / 60000);
  const h = Math.round(abs / 3600000);
  const days = Math.round(abs / 86400000);
  const sign = diffMs < 0 ? "ago" : "in";
  if (m < 60) return `${sign === "ago" ? m + "m ago" : "in " + m + "m"}`;
  if (h < 48) return `${sign === "ago" ? h + "h ago" : "in " + h + "h"}`;
  return `${sign === "ago" ? days + "d ago" : "in " + days + "d"}`;
}

function CreateTaskDialog({ open, onOpenChange, leads, onCreated, defaultLeadId }) {
  const [form, setForm] = useState({
    lead_id: defaultLeadId || "",
    title: "",
    type: "call_back",
    due_at: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        lead_id: defaultLeadId || "",
        title: "",
        type: "call_back",
        due_at: "",
        notes: "",
      });
    }
  }, [open, defaultLeadId]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.lead_id) return toast.error("Pick a lead.");
    if (!form.title.trim()) return toast.error("Title is required.");
    if (!form.due_at) return toast.error("Pick a due date/time.");
    setSubmitting(true);
    try {
      const due_iso = new Date(form.due_at).toISOString();
      const r = await api.post("/admin/tasks", { ...form, due_at: due_iso });
      toast.success("Task created.");
      onCreated?.(r.data.task);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="task-create-dialog">
        <DialogHeader>
          <DialogTitle>New task</DialogTitle>
          <DialogDescription>
            Tasks help you keep follow-ups, callbacks, and document hand-offs on schedule.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <Label>Lead</Label>
            <Select value={form.lead_id} onValueChange={(v) => setForm((p) => ({ ...p, lead_id: v }))}>
              <SelectTrigger data-testid="task-lead">
                <SelectValue placeholder="Select a lead…" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {leads.map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.name} — {l.preferred_country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="task-title">Title</Label>
            <Input
              id="task-title"
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              placeholder="Call about scholarship slabs"
              required
              minLength={2}
              data-testid="task-title"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm((p) => ({ ...p, type: v }))}>
                <SelectTrigger data-testid="task-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TASK_TYPES.map((t) => (
                    <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="task-due">Due at</Label>
              <Input
                id="task-due"
                type="datetime-local"
                value={form.due_at}
                onChange={(e) => setForm((p) => ({ ...p, due_at: e.target.value }))}
                required
                data-testid="task-due"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="task-notes">Notes</Label>
            <Textarea
              id="task-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
              data-testid="task-notes"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}
              className="bg-[#0A2540] text-white hover:bg-[#1E3A5F]" data-testid="task-create-submit">
              {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating…</> : "Create task"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TaskRow({ task, onComplete, onDelete }) {
  const isDone = task.status === "done";
  return (
    <div
      className={`flex items-start gap-3 rounded-md border border-slate-200 bg-white p-3 transition-shadow hover:shadow-sm ${isDone ? "opacity-60" : ""}`}
      data-testid={`task-row-${task.id}`}
    >
      <button
        onClick={() => !isDone && onComplete(task)}
        className={`mt-0.5 h-5 w-5 shrink-0 rounded-full border ${isDone ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-300 hover:border-emerald-500"}`}
        title={isDone ? "Done" : "Mark complete"}
        data-testid={`task-complete-${task.id}`}
      >
        {isDone && <CheckCircle2 className="h-4 w-4" />}
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={`truncate text-sm font-medium text-[#0A2540] ${isDone ? "line-through" : ""}`}>
            {task.title}
          </span>
          {task.auto_created && (
            <span title="Auto-created" className="inline-flex items-center gap-0.5 rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">
              <Sparkles className="h-2.5 w-2.5" /> auto
            </span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
          <span className="rounded bg-slate-100 px-1.5 py-0.5 capitalize">
            {task.type.replace("_", " ")}
          </span>
          <span className="text-slate-400">·</span>
          <span title={task.due_at}>{relTime(task.due_at)}</span>
          {task.lead_name && (
            <>
              <span className="text-slate-400">·</span>
              <span className="truncate">{task.lead_name}</span>
            </>
          )}
          {task.assigned_to_name && (
            <>
              <span className="text-slate-400">·</span>
              <span>@{task.assigned_to_name.split(" ")[0]}</span>
            </>
          )}
        </div>
        {task.notes && (
          <p className="mt-1 text-xs text-slate-600 line-clamp-2">{task.notes}</p>
        )}
      </div>
      {!isDone && (
        <button
          onClick={() => onDelete(task)}
          className="ml-1 text-slate-300 hover:text-rose-500"
          title="Delete"
          data-testid={`task-delete-${task.id}`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export function TasksPanel({ open, onOpenChange, leads }) {
  const [data, setData] = useState({ tasks: [], counts: {}, total: 0 });
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [scope, setScope] = useState("mine");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/tasks", { params: { scope } });
      setData(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scope]);

  const onComplete = async (task) => {
    try {
      await api.patch(`/admin/tasks/${task.id}`, { status: "done" });
      toast.success("Task completed.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onDelete = async (task) => {
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    try {
      await api.delete(`/admin/tasks/${task.id}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const grouped = useMemo(() => {
    const out = { overdue: [], today: [], this_week: [], later: [], done: [] };
    for (const t of data.tasks || []) {
      if (out[t.bucket]) out[t.bucket].push(t);
    }
    return out;
  }, [data]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto px-6 sm:max-w-xl"
        data-testid="tasks-panel"
      >
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2 tracking-tight">
            <ClipboardList className="h-5 w-5 text-[#0A2540]" /> My Tasks · {data.total}
          </SheetTitle>
          <SheetDescription>
            Follow-ups, callbacks, and document hand-offs. Auto-tasks appear when leads
            move to Follow-up or finish the test.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 flex items-center justify-between gap-2">
          <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5 text-xs">
            {["mine", "unassigned", "all"].map((s) => (
              <button
                key={s}
                onClick={() => setScope(s)}
                className={`rounded px-2.5 py-1 transition-colors ${scope === s ? "bg-[#0A2540] text-white" : "text-slate-600 hover:text-[#0A2540]"}`}
                data-testid={`task-scope-${s}`}
              >
                {s === "mine" ? "Mine" : s === "unassigned" ? "Unassigned" : "All"}
              </button>
            ))}
          </div>
          <Button
            size="sm"
            onClick={() => setCreateOpen(true)}
            className="bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
            data-testid="task-new-btn"
          >
            <Plus className="mr-1 h-4 w-4" /> New task
          </Button>
        </div>

        <div className="mt-4 grid grid-cols-5 gap-1.5">
          {BUCKETS.map((b) => (
            <div
              key={b.k}
              className={`rounded border px-2 py-1.5 text-center ${b.accent}`}
              data-testid={`task-count-${b.k}`}
            >
              <div className="text-base font-semibold tabular-nums">
                {data.counts?.[b.k] || 0}
              </div>
              <div className="text-[9px] uppercase tracking-wider">{b.l}</div>
            </div>
          ))}
        </div>

        {loading && (
          <div className="mt-6 flex items-center justify-center py-10 text-sm text-slate-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading tasks…
          </div>
        )}

        {!loading && (
          <div className="mt-5 space-y-5 pb-10">
            {BUCKETS.map((b) => {
              const items = grouped[b.k] || [];
              if (items.length === 0) return null;
              const Icon = b.icon;
              return (
                <section key={b.k} data-testid={`task-bucket-${b.k}`}>
                  <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-600">
                    <Icon className="h-3.5 w-3.5" /> {b.l}
                    <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[10px]">
                      {items.length}
                    </Badge>
                  </h3>
                  <div className="space-y-2">
                    {items.map((t) => (
                      <TaskRow key={t.id} task={t} onComplete={onComplete} onDelete={onDelete} />
                    ))}
                  </div>
                </section>
              );
            })}
            {data.total === 0 && (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 py-10 text-center">
                <ClipboardList className="h-7 w-7 text-slate-300" />
                <p className="mt-2 text-sm font-medium text-[#0A2540]">No tasks yet</p>
                <p className="mt-1 text-xs text-slate-500">
                  Create one or move a lead to Follow-up — auto-tasks will appear here.
                </p>
              </div>
            )}
          </div>
        )}

        <CreateTaskDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          leads={leads || []}
          onCreated={load}
        />
      </SheetContent>
    </Sheet>
  );
}

export default TasksPanel;
